import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { MapIcon, Scissors, Clock, Star, Loader2, Trophy, Image as ImageIcon, Copy, Check, Smartphone, Link, QrCode, Wallet, Settings } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import QRCode from 'qrcode';

// Components
import SalonProfile from '@/components/salon/SalonProfile';
import SalonServices from '@/components/salon/SalonServices';
import SalonHours from '@/components/salon/SalonHours';
import SalonReviews from '@/components/salon/SalonReviews';
import SalonTrophies from '@/components/salon/SalonTrophies';
import SalonGallery from '@/components/salon/SalonGallery';
import SalonCosts from '@/components/salon/SalonCosts';
import SalonSettings from '@/components/salon/SalonSettings';

const Salon: React.FC = () => {
    const { t } = useTranslation();
    const { organization, isAdmin, isOwner } = useAuth();

    const [activeTab, setActiveTab] = useState<'profile' | 'services' | 'hours' | 'reviews' | 'trophies' | 'gallery' | 'costs' | 'settings'>('profile');
    const [isLoading, setIsLoading] = useState(true);
    const [isCopied, setIsCopied] = useState(false);
    const [isGeneratingQR, setIsGeneratingQR] = useState(false);

    const isReadOnly = !isAdmin;

    useEffect(() => {
        if (organization) {
            setIsLoading(false);
        }
    }, [organization]);

    const tabContentVariants = {
        hidden: { opacity: 0, y: 10 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
        exit: { opacity: 0, y: -10, transition: { duration: 0.2 } }
    };

    if (isLoading) {
        return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-gray-400" /></div>;
    }

    const handleCopyDownloadLink = () => {
        if (!organization?.id) return;
        const link = `${window.location.origin}/download/${organization.id}`;

        const fallbackCopyTextToClipboard = (text: string) => {
            const textArea = document.createElement("textarea");
            textArea.value = text;
            // Prevent scrolling to bottom of page in MS Edge.
            textArea.style.top = "0";
            textArea.style.left = "0";
            textArea.style.position = "fixed";

            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();

            try {
                const successful = document.execCommand('copy');
                if (successful) {
                    setIsCopied(true);
                    toast.success('Link copiato negli appunti!');
                    setTimeout(() => setIsCopied(false), 2000);
                } else {
                    throw new Error('Fallback copy failed');
                }
            } catch (err) {
                console.error('Fallback: Oops, unable to copy', err);
                toast.error('Errore durante la copia del link');
            }

            document.body.removeChild(textArea);
        }

        if (!navigator.clipboard) {
            fallbackCopyTextToClipboard(link);
            return;
        }

        navigator.clipboard.writeText(link)
            .then(() => {
                setIsCopied(true);
                toast.success('Link copiato negli appunti!');
                setTimeout(() => setIsCopied(false), 2000);
            })
            .catch((err) => {
                console.error('Failed to copy link via clipboard API: ', err);
                // Prova col fallback se le API falliscono (molto comune su http non localhost o webviews)
                fallbackCopyTextToClipboard(link);
            });
    };

    const handleDownloadQR = async () => {
        if (!organization?.id) return;
        try {
            setIsGeneratingQR(true);
            const link = `${window.location.origin}/download/${organization.id}`;
            const qrDataUrl = await QRCode.toDataURL(link, {
                width: 500,
                margin: 2,
                color: {
                    dark: '#111827', // text-gray-900
                    light: '#FFFFFF'
                }
            });

            // Create a fake anchor to trigger download
            const anchor = document.createElement('a');
            anchor.href = qrDataUrl;
            anchor.download = `QR_${organization.name || 'Salone'}.png`;
            document.body.appendChild(anchor);
            anchor.click();
            document.body.removeChild(anchor);

            toast.success(t('salon.qr_download_success') || 'QR Code scaricato!');
        } catch (error) {
            console.error('Failed to generate QR code:', error);
            toast.error(t('salon.qr_download_error') || 'Errore durante la generazione del QR Code');
        } finally {
            setIsGeneratingQR(false);
        }
    };

    return (
        <div className="max-w-5xl mx-auto space-y-6">

            {/* Header */}
            <div className="flex flex-row items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{t('salon.title') || 'Il tuo Salone'}</h1>
                    <p className="text-gray-500 mt-1 text-sm sm:text-base">{t('salon.subtitle') || 'Gestisci le informazioni del negozio'}</p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={handleDownloadQR}
                        disabled={isGeneratingQR}
                        className="flex-shrink-0 flex items-center justify-center gap-2 w-11 h-11 sm:w-auto sm:h-auto sm:px-4 sm:py-2.5 bg-white text-gray-900 border border-gray-200 rounded-xl font-bold shadow-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors active:scale-95 group"
                        title={t('salon.download_qr') || 'Scarica QR'}
                    >
                        {isGeneratingQR ? (
                            <Loader2 size={18} className="animate-spin text-gray-500" />
                        ) : (
                            <QrCode size={18} />
                        )}
                        <span className="hidden sm:inline">{t('salon.download_qr') || 'Scarica QR'}</span>
                    </button>
                    <button
                        onClick={handleCopyDownloadLink}
                        className="flex-shrink-0 flex items-center justify-center gap-2 w-11 h-11 sm:w-auto sm:h-auto sm:px-4 sm:py-2.5 bg-gray-900 text-white rounded-xl font-bold shadow-lg hover:bg-black transition-colors active:scale-95 group"
                        title={t('salon.generate_link')}
                    >
                        {isCopied ? (
                            <Check size={18} className="text-green-400" />
                        ) : (
                            <>
                                <Link size={18} className="sm:hidden" />
                                <Copy size={18} className="hidden sm:block" />
                            </>
                        )}
                        <span className="hidden sm:inline">{t('salon.generate_link')}</span>
                    </button>
                </div>
            </div>

            {/* Navigation Tabs */}
            <div className="bg-white rounded-2xl p-1.5 border border-gray-100 shadow-sm inline-flex flex-wrap gap-1 max-w-full">
                {[
                    { id: 'profile', label: t('salon.tabs.profile'), icon: MapIcon },
                    { id: 'services', label: t('salon.tabs.services'), icon: Scissors },
                    { id: 'hours', label: t('salon.tabs.hours'), icon: Clock },
                    { id: 'gallery', label: t('salon.tabs.gallery'), icon: ImageIcon },
                    { id: 'trophies', label: t('salon.tabs.trophies'), icon: Trophy },
                    { id: 'reviews', label: t('salon.tabs.reviews'), icon: Star },
                    // Costi: solo per il titolare (dati economici)
                    ...(isOwner ? [{ id: 'costs', label: 'Costi', icon: Wallet }] : []),
                    // Impostazioni sempre come ultimo elemento
                    { id: 'settings', label: t('salon.tabs.settings'), icon: Settings },
                ].map((tab) => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id as any)}
                        className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all whitespace-nowrap ${activeTab === tab.id
                            ? 'bg-clip-500 text-white shadow-md shadow-clip-200'
                            : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900'
                            }`}
                    >
                        {/* @ts-ignore */}
                        <tab.icon size={16} />
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Tab Content — una sola motion.div rimontata al cambio tab (key).
                Niente AnimatePresence/exit: con framer via importmap + React 19 l'exit
                di mode='wait' poteva non completare mai, lasciando il vecchio tab a schermo. */}
            <motion.div key={activeTab} variants={tabContentVariants} initial="hidden" animate="visible">
                {activeTab === 'profile' && <SalonProfile organization={organization} readOnly={isReadOnly} />}
                {activeTab === 'settings' && <SalonSettings organization={organization} readOnly={isReadOnly} />}
                {activeTab === 'services' && <SalonServices organizationId={organization?.id} />}
                {activeTab === 'hours' && <SalonHours organization={organization} readOnly={isReadOnly} />}
                {activeTab === 'gallery' && <SalonGallery />}
                {activeTab === 'trophies' && <SalonTrophies />}
                {activeTab === 'reviews' && <SalonReviews />}
                {activeTab === 'costs' && isOwner && <SalonCosts />}
            </motion.div>
        </div>
    );
};

export default Salon;